import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ProjectTable, type ProjectRow } from "@/components/projects/project-table";
import { ProjectDrawer } from "@/components/projects/project-drawer";
import { JoinProjectDialog } from "@/components/projects/join-project-dialog";
import { apiGet, apiPatch, apiDelete } from "@/lib/api";
import { FolderKanban, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { deleteWithUndo } from "@/lib/delete-with-undo";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export const Route = createFileRoute("/_authenticated/projects/")({
  component: ProjectsPage,
});

type StatusFilter = "active" | "archived" | "all";

interface ProjectsResponse {
  data: ProjectRow[];
  total: number;
  page: number;
  limit: number;
}

function ProjectsPage() {
  const { user } = Route.useRouteContext();
  const queryClient = useQueryClient();
  const isAdmin = user.role === "admin" || user.role === "div_admin";

  const [statusFilter, setStatusFilter] = useState<StatusFilter>("active");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [joinOpen, setJoinOpen] = useState(false);
  // Asked to delete (waiting for the confirmation), and deleted but still within the 10-second undo window.
  const [deleteAsk, setDeleteAsk] = useState<ProjectRow | null>(null);
  const [pendingDelete, setPendingDelete] = useState<string[]>([]);
  const [editProject, setEditProject] = useState<ProjectRow | null>(null);

  // Fetch projects
  const { data: projectsData, isLoading } = useQuery({
    queryKey: ["projects", statusFilter],
    queryFn: () =>
      apiGet<ProjectsResponse>(
        `/api/projects?status=${statusFilter}`
      ),
  });

  // Archive mutation
  const archiveMutation = useMutation({
    mutationFn: (projectId: string) =>
      apiPatch<{ data: ProjectRow }>(`/api/projects/${projectId}/archive`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      toast.success("Project completed");
    },
    onError: () => {
      toast.error("Failed to archive project");
    },
  });

  // Activate mutation
  const activateMutation = useMutation({
    mutationFn: (projectId: string) =>
      apiPatch<{ data: ProjectRow }>(`/api/projects/${projectId}/activate`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      toast.success("Project reactivated");
    },
    onError: () => {
      toast.error("Failed to reactivate project");
    },
  });

  // Delete mutation -- blocked by default if the project has linked records
  // (counts + message come back on a 403); re-run with force=true to confirm.
  const deleteMutation = useMutation({
    mutationFn: ({ projectId, force }: { projectId: string; force?: boolean }) =>
      apiDelete(`/api/projects/${projectId}${force ? "?force=true" : ""}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      toast.success("Project deleted");
    },
    onError: (err: Error, variables) => {
      if (!variables.force) {
        if (
          window.confirm(
            `${err.message}\n\nThis permanently deletes the project and everything under it. Continue?`
          )
        ) {
          deleteMutation.mutate({ projectId: variables.projectId, force: true });
        }
        return;
      }
      toast.error(err.message || "Failed to delete project");
    },
  });

  // Step 1: ask. Step 2 (after confirming): the project leaves the list at once and is only really
  // deleted after 10 seconds, so Undo brings it straight back.
  function handleDelete(project: ProjectRow) {
    setDeleteAsk(project);
  }

  function confirmDelete(project: ProjectRow) {
    setPendingDelete((ids) => [...ids, project.id]);
    const release = () => setPendingDelete((ids) => ids.filter((id) => id !== project.id));
    deleteWithUndo(
      project.name,
      async () => {
        try {
          await deleteMutation.mutateAsync({ projectId: project.id });
        } finally {
          release();
        }
      },
      release
    );
  }

  const projects = (projectsData?.data || []).filter((p) => !pendingDelete.includes(p.id));

  function handleEdit(project: ProjectRow) {
    setEditProject(project);
    setDrawerOpen(true);
  }

  function handleCreate() {
    setEditProject(null);
    setDrawerOpen(true);
  }

  function handleArchive(project: ProjectRow) {
    archiveMutation.mutate(project.id);
  }

  function handleActivate(project: ProjectRow) {
    activateMutation.mutate(project.id);
  }

  const statusTabs: { label: string; value: StatusFilter }[] = [
    { label: "Active", value: "active" },
    { label: "Archived", value: "archived" },
    { label: "All", value: "all" },
  ];

  return (
    <div className="space-y-4 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between pt-2">
        <h1 className="text-2xl font-bold text-text-primary">Projects</h1>
        {isAdmin && (
          <div className="flex gap-2">
            {user.role === "div_admin" && (
              <Button variant="outline" className="h-11" onClick={() => setJoinOpen(true)}>
                Join a project
              </Button>
            )}
            <Button
              className="h-11 bg-paris-red hover:bg-paris-red-dark"
              onClick={handleCreate}
            >
              <Plus className="h-4 w-4 mr-1.5" />
              Create Project
            </Button>
          </div>
        )}
      </div>

      {/* Status filter tabs (admin only) */}
      {isAdmin && (
        <div className="flex gap-1 bg-surface-hover/50 rounded-lg p-1">
          {statusTabs.map((tab) => (
            <button
              key={tab.value}
              className={cn(
                "flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors min-h-[40px]",
                statusFilter === tab.value
                  ? "bg-paris-red text-white shadow-sm"
                  : "text-text-tertiary hover:text-text-primary"
              )}
              onClick={() => setStatusFilter(tab.value)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}

      {/* Content */}
      {!isLoading && projects.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <FolderKanban className="h-12 w-12 text-muted-foreground/40 mb-3" />
            {isAdmin ? (
              <>
                <p className="text-lg font-medium text-muted-foreground">
                  No projects yet
                </p>
                <p className="text-sm text-muted-foreground/70 mt-1 mb-4">
                  Create your first project to get started
                </p>
                <Button
                  className="h-12 bg-paris-red hover:bg-paris-red-dark"
                  onClick={handleCreate}
                >
                  <Plus className="h-4 w-4 mr-1.5" />
                  Create Your First Project
                </Button>
              </>
            ) : (
              <>
                <p className="text-lg font-medium text-muted-foreground">
                  No projects assigned
                </p>
                <p className="text-sm text-muted-foreground/70 mt-1">
                  You haven't been assigned to any projects yet.
                  <br />
                  Ask your admin to add you to a crew.
                </p>
              </>
            )}
          </CardContent>
        </Card>
      ) : (
        <ProjectTable
          projects={projects}
          isLoading={isLoading}
          userRole={user.role || "field_personnel"}
          onEdit={handleEdit}
          onArchive={handleArchive}
          onActivate={handleActivate}
          onDelete={handleDelete}
        />
      )}

      <JoinProjectDialog open={joinOpen} onOpenChange={setJoinOpen} />

      <AlertDialog open={!!deleteAsk} onOpenChange={(v) => !v && setDeleteAsk(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleteAsk?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              The project leaves the list right away and is deleted for good after 10 seconds. You can press Undo
              in the message that appears until then. If it has linked records (FLHAs, crews, incidents...), you
              will be asked once more before they are removed too.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep project</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive hover:bg-destructive/90"
              onClick={() => {
                if (!deleteAsk) return;
                const target = deleteAsk;
                setDeleteAsk(null);
                confirmDelete(target);
              }}
            >
              Delete project
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Side drawer */}
      <ProjectDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        project={editProject}
        userRole={user.role || "field_personnel"}
      />
    </div>
  );
}
